import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  Building2, Plus, Search, Users, FileText, Settings,
  ChevronRight, Shield, AlertCircle, CheckCircle, Eye,
  Loader2, Edit, MoreVertical, Trash2, AlertTriangle, X
} from 'lucide-react'
import { useAuth } from '../../../contexts/AuthContext'
import { toast } from 'sonner'

const API_URL = process.env.REACT_APP_BACKEND_URL

const STATUT_CONFIG = {
  actif: { label: 'Actif', color: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' },
  inactif: { label: 'Inactif', color: 'bg-red-100 text-red-700', dot: 'bg-red-500' },
  prospect: { label: 'Prospect', color: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' }
}

export default function AdminClients() {
  const navigate = useNavigate()
  const { profile } = useAuth()
  const [tenants, setTenants] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [purgeTarget, setPurgeTarget] = useState(null)

  const isMasterAdmin = profile?.role === 'master_admin'
  const canAccess = isMasterAdmin || profile?.role === 'c2_admin'

  // Auto-reopen modal if there's a draft
  useEffect(() => {
    try {
      const draft = sessionStorage.getItem('c2_create_client_draft')
      if (draft) {
        const parsed = JSON.parse(draft)
        if (parsed.nom || parsed.siren || parsed.secteur_activite) {
          setShowCreateModal(true)
        }
      }
    } catch {}
  }, [])

  const fetchTenants = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/tenants`)
      if (response.ok) {
        const data = await response.json()
        setTenants(data.tenants || [])
      }
    } catch (err) {
      setError('Erreur lors du chargement')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (canAccess) {
      fetchTenants()
    }
  }, [fetchTenants, canAccess])

  const filteredTenants = tenants.filter(t => {
    if (!searchTerm) return true
    const search = searchTerm.toLowerCase()
    return t.nom?.toLowerCase().includes(search) || 
           t.siren?.includes(search) ||
           t.secteur_activite?.toLowerCase().includes(search)
  })

  // Stats
  const stats = {
    total: tenants.length,
    actifs: tenants.filter(t => t.statut === 'actif').length,
    avecSettings: tenants.filter(t => t.tenant_settings).length
  }

  if (!canAccess) {
    return (
      <div className="p-8 text-center">
        <Shield className="w-12 h-12 mx-auto text-red-400 mb-4" />
        <h2 className="text-lg font-semibold text-slate-800 mb-2">Accès refusé</h2>
        <p className="text-slate-500">Vous n'avez pas les droits pour accéder à cette page.</p>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6" data-testid="admin-clients-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Master Admin Menu</h1>
          <p className="text-sm text-slate-500">Gestion des tenants et paramètres</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
          data-testid="create-tenant-btn"
        >
          <Plus className="w-4 h-4" />
          Nouveau client
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{stats.total}</p>
              <p className="text-xs text-slate-500">Total clients</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{stats.actifs}</p>
              <p className="text-xs text-slate-500">Actifs</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Settings className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{stats.avecSettings}</p>
              <p className="text-xs text-slate-500">Configurés</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Rechercher un client..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>
      </div>

      {/* Tenants Grid */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredTenants.map(tenant => {
          const statutConfig = STATUT_CONFIG[tenant.statut] || STATUT_CONFIG.actif
          const settings = tenant.tenant_settings?.[0] || tenant.tenant_settings
          
          return (
            <div 
              key={tenant.id}
              className="bg-white rounded-xl border border-slate-200 hover:border-indigo-200 hover:shadow-md transition-all cursor-pointer group"
              onClick={() => navigate(`/admin/clients/${tenant.id}`)}
              data-testid={`tenant-${tenant.id}`}
            >
              <div className="p-5">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-100 to-blue-100 flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-indigo-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-800 group-hover:text-indigo-600 transition-colors">
                        {tenant.nom}
                      </h3>
                      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium rounded-full ${statutConfig.color}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${statutConfig.dot}`}></span>
                        {statutConfig.label}
                      </span>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-indigo-400 transition-colors" />
                </div>

                <div className="space-y-2 text-sm">
                  {tenant.siren && (
                    <div className="flex items-center justify-between text-slate-600">
                      <span>SIREN</span>
                      <span className="font-mono">{tenant.siren}</span>
                    </div>
                  )}
                  {tenant.secteur_activite && (
                    <div className="flex items-center justify-between text-slate-600">
                      <span>Secteur</span>
                      <span className="truncate max-w-[150px]">{tenant.secteur_activite}</span>
                    </div>
                  )}
                  {settings && (
                    <div className="flex items-center justify-between text-slate-600">
                      <span>Budget amiable</span>
                      <span className="font-mono">
                        {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(settings.budget_amiable_annuel || 0)}
                      </span>
                    </div>
                  )}
                </div>

                <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                  <div className="flex items-center gap-4">
                    <span className="flex items-center gap-1">
                      <Users className="w-3.5 h-3.5" />
                      {tenant.user_profiles?.length || 0} utilisateurs
                    </span>
                    <span className="flex items-center gap-1">
                      <FileText className="w-3.5 h-3.5" />
                      {tenant.polices?.length || 0} polices
                    </span>
                  </div>
                  {isMasterAdmin && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        setPurgeTarget(tenant)
                      }}
                      className="flex items-center gap-1 px-2 py-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                      data-testid={`purge-tenant-btn-${tenant.id}`}
                      title="Supprimer définitivement ce client"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      <span className="hidden sm:inline">Supprimer</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {filteredTenants.length === 0 && (
        <div className="text-center py-12">
          <Building2 className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">Aucun client trouvé</p>
        </div>
      )}

      {/* Modal création */}
      {showCreateModal && (
        <CreateTenantModal
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            setShowCreateModal(false)
            fetchTenants()
          }}
        />
      )}

      {/* Modal purge / suppression définitive */}
      {purgeTarget && (
        <PurgeTenantModal
          tenant={purgeTarget}
          onClose={() => setPurgeTarget(null)}
          onPurged={() => {
            setPurgeTarget(null)
            fetchTenants()
          }}
        />
      )}
    </div>
  )
}

// Modal de création de tenant
function CreateTenantModal({ onClose, onCreated }) {
  const [saving, setSaving] = useState(false)
  const DRAFT_KEY = 'c2_create_client_draft'
  const [formData, setFormData] = useState(() => {
    try {
      const draft = sessionStorage.getItem(DRAFT_KEY)
      return draft ? JSON.parse(draft) : { nom: '', siren: '', secteur_activite: '' }
    } catch { return { nom: '', siren: '', secteur_activite: '' } }
  })

  // Persist draft on every change
  useEffect(() => {
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify(formData))
  }, [formData])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!formData.nom) return
    
    setSaving(true)
    try {
      const response = await fetch(`${API_URL}/api/admin/tenants`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      })
      
      if (response.ok) {
        sessionStorage.removeItem(DRAFT_KEY)
        toast.success('Client créé')
        onCreated()
      } else {
        const err = await response.json()
        toast.error(err.detail || 'Erreur')
      }
    } catch (err) {
      toast.error('Erreur lors de la création')
    } finally {
      setSaving(false)
    }
  }

  const handleClose = () => {
    // Keep draft in sessionStorage, don't clear it
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={handleClose}>
      <div 
        className="bg-white rounded-xl shadow-xl max-w-md w-full"
        onClick={e => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-800">Nouveau client</h3>
          {(formData.nom || formData.siren || formData.secteur_activite) && (
            <span className="text-xs bg-amber-100 text-amber-600 px-2 py-0.5 rounded-full">Brouillon sauvegardé</span>
          )}
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Nom *</label>
            <input
              type="text"
              value={formData.nom}
              onChange={e => setFormData({...formData, nom: e.target.value})}
              required
              placeholder="Nom de l'entreprise"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">SIREN</label>
            <input
              type="text"
              value={formData.siren}
              onChange={e => setFormData({...formData, siren: e.target.value})}
              placeholder="123456789"
              maxLength={9}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Secteur d'activité</label>
            <input
              type="text"
              value={formData.secteur_activite}
              onChange={e => setFormData({...formData, secteur_activite: e.target.value})}
              placeholder="Transport, Industrie..."
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
            />
          </div>
          
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={saving || !formData.nom}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              Créer
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Modal de confirmation de purge (suppression définitive)
function PurgeTenantModal({ tenant, onClose, onPurged }) {
  const [confirmation, setConfirmation] = useState('')
  const [purging, setPurging] = useState(false)
  const [rapport, setRapport] = useState(null)

  const isConfirmed = confirmation === 'SUPPRIMER'

  const handlePurge = async () => {
    if (!isConfirmed) return
    setPurging(true)

    try {
      const response = await fetch(`${API_URL}/api/admin/tenants/${tenant.id}/purge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirmation: 'SUPPRIMER' })
      })

      if (response.ok) {
        const data = await response.json()
        setRapport(data.rapport)
        toast.success(`Client "${tenant.nom}" supprimé définitivement`)
        setTimeout(() => onPurged(), 2000)
      } else {
        const err = await response.json()
        toast.error(err.detail || 'Erreur lors de la suppression')
      }
    } catch (err) {
      toast.error('Erreur de connexion')
    } finally {
      setPurging(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-2xl max-w-lg w-full"
        onClick={e => e.stopPropagation()}
        data-testid="purge-modal"
      >
        {rapport ? (
          <div className="p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-800">Suppression terminée</h3>
                <p className="text-sm text-slate-500">Client "{rapport.tenant}" et toutes ses données ont été supprimés.</p>
              </div>
            </div>
            <div className="bg-slate-50 rounded-lg p-3 text-xs font-mono space-y-1 max-h-48 overflow-y-auto">
              {Object.entries(rapport.supprime || {}).map(([table, count]) => (
                <div key={table} className="flex justify-between">
                  <span className="text-slate-600">{table}</span>
                  <span className="text-slate-800 font-medium">{count}</span>
                </div>
              ))}
              {rapport.erreurs?.length > 0 && (
                <div className="pt-2 border-t border-slate-200 mt-2">
                  <p className="text-amber-600 font-semibold mb-1">Avertissements :</p>
                  {rapport.erreurs.map((e, i) => (
                    <p key={i} className="text-amber-500 break-all">{e}</p>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          <>
            <div className="px-6 py-4 border-b border-red-100 bg-red-50 rounded-t-xl flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                <h3 className="text-lg font-semibold text-red-800">Supprimer définitivement</h3>
              </div>
              <button onClick={onClose} className="p-1 text-red-400 hover:text-red-600 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-800 font-medium">
                  Attention : cette action supprimera définitivement toutes les données du client "{tenant.nom}".
                </p>
                <ul className="mt-2 text-xs text-red-600 space-y-1 list-disc list-inside">
                  <li>Polices, sinistres, primes</li>
                  <li>Véhicules, sites, chauffeurs</li>
                  <li>Documents, base documentaire</li>
                  <li>Utilisateurs et comptes rattachés</li>
                  <li>Alertes, recommandations, scoring</li>
                </ul>
                <p className="mt-3 text-xs text-red-700 font-semibold">
                  Cette action est irréversible.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Tapez <span className="font-mono font-bold text-red-600">SUPPRIMER</span> pour confirmer
                </label>
                <input
                  type="text"
                  value={confirmation}
                  onChange={e => setConfirmation(e.target.value)}
                  placeholder="SUPPRIMER"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  data-testid="purge-confirmation-input"
                  autoComplete="off"
                />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg"
                  data-testid="purge-cancel-btn"
                >
                  Annuler
                </button>
                <button
                  onClick={handlePurge}
                  disabled={!isConfirmed || purging}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  data-testid="purge-confirm-btn"
                >
                  {purging ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Suppression...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      Supprimer définitivement
                    </>
                  )}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
